import { openEncryptedParams, sealEncryptedParams } from '@/utils/chat_entry_crypto';
import { readChatSession } from '@/utils/chat_session_storage';
import type { LocationQuery, LocationQueryValue } from 'vue-router';

const CHAT_RUNTIME_QUERY_PARAMS_KEY = 'gx_chat_runtime_query_params';

type RuntimeParams = Record<string, string>;

function normalizeFirstValue(
  value: LocationQueryValue | LocationQueryValue[] | undefined
): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value[0] ?? undefined;
  }
  return value;
}

export function writeRuntimeQueryParamsFromRouteQuery(query: LocationQuery): void {
  const nextParams: RuntimeParams = {};
  Object.keys(query).forEach((key) => {
    const value = normalizeFirstValue(query[key]);
    if (value) {
      nextParams[key] = value;
    }
  });
  writeRuntimeQueryParams(nextParams);
}

export function writeRuntimeQueryParams(params: RuntimeParams): void {
  const nextParams: RuntimeParams = {};
  Object.keys(params).forEach((key) => {
    if (params[key]) {
      nextParams[key] = params[key];
    }
  });
  if (Object.keys(nextParams).length === 0) {
    return;
  }
  sessionStorage.setItem(CHAT_RUNTIME_QUERY_PARAMS_KEY, sealEncryptedParams(nextParams));
}

function readLegacyPlainRuntimeParams(raw: string): RuntimeParams | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const o = parsed as Record<string, unknown>;
    if (o.v === 1 && typeof o.enc === 'string') {
      return null;
    }
    const out: RuntimeParams = {};
    Object.keys(o).forEach((key) => {
      const value = o[key];
      if (value !== undefined && value !== null && String(value) !== '') {
        out[key] = String(value);
      }
    });
    return out;
  } catch {
    return null;
  }
}

function readRuntimeQueryParams(): RuntimeParams {
  try {
    const raw = sessionStorage.getItem(CHAT_RUNTIME_QUERY_PARAMS_KEY);
    if (!raw) {
      return {};
    }
    const decrypted = openEncryptedParams(raw);
    if (decrypted) {
      return decrypted;
    }
    return readLegacyPlainRuntimeParams(raw) ?? {};
  } catch {
    return {};
  }
}

function getRuntimeParam(keys: string[]): string | undefined {
  const stored = readRuntimeQueryParams();
  for (const key of keys) {
    if (stored[key]) {
      return stored[key];
    }
  }
  const lowerKeys = keys.map((item) => item.toLowerCase());
  for (const key of Object.keys(stored)) {
    if (lowerKeys.includes(key.toLowerCase()) && stored[key]) {
      return stored[key];
    }
  }
  return undefined;
}

export function getRuntimeSourceUrl(): string | undefined {
  return getRuntimeParam(['source', 'sourceUrl', 'entrySource']);
}

export function getRuntimeUserID(): string | undefined {
  return getRuntimeParam(['userID', 'userId', 'userid']) ?? readChatSession()?.userID;
}

export function getRuntimeToken(): string | undefined {
  return getRuntimeParam(['token']) ?? readChatSession()?.token;
}

export function getRuntimeMacAddress(): string | undefined {
  return getRuntimeParam(['macAddress', 'macaddress']) ?? readChatSession()?.macAddress;
}

export function getRuntimeToAccount(): string | undefined {
  return getRuntimeParam(['toAccount', 'toaccount']);
}

export function hasRuntimeQueryFlag(keys: string[]): boolean {
  return Boolean(getRuntimeParam(keys));
}

/** True when runtime entry uses `type=tk_oss` (or same under businessType/channel), not a param literally named `tk_oss`. */
export function isRuntimeChatType(expected: string): boolean {
  const value = getRuntimeParam(['type', 'businessType', 'channel']);
  return value === expected;
}
